/**
 * Dock layout state for the map workspace: which panels sit in which dock (tab order),
 * dock sizes / collapsed flags and floating-window rectangles. Pure reducer and
 * normaliser (no React; the localStorage helpers are guarded) so it is unit tested.
 * Persisted under LAYOUT_KEY by hooks/useDockLayout.js; any shape change needs a
 * lib/stateMigrations.js entry.
 */
import { DOCKS, FLOAT, PANELS, getPanel, defaultFloatRect } from './panel-registry';

export const LAYOUT_KEY = 'ultimate-gis:layout:v1';
export const LAYOUT_VERSION = 1;
export const DEFAULT_SIZES = { left: 18, right: 22, bottom: 32 };
export const SIZE_LIMITS = { left: [10, 45], right: [10, 45], bottom: [10, 70] };
export const MIN_FLOAT = { w: 200, h: 120 };

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const num = (v) => typeof v === 'number' && Number.isFinite(v);

export function clampSize(dock, size) {
  const [lo, hi] = SIZE_LIMITS[dock] || [10, 70];
  return Math.min(hi, Math.max(lo, size));
}

export function cleanRect(r) {
  if (!isObj(r) || !num(r.x) || !num(r.y) || !num(r.w) || !num(r.h)) return null;
  return { x: Math.round(r.x), y: Math.round(r.y), w: Math.max(MIN_FLOAT.w, Math.round(r.w)), h: Math.max(MIN_FLOAT.h, Math.round(r.h)) };
}

export function createDefaultLayout(panels = PANELS) {
  const tabs = { left: [], right: [], bottom: [] };
  const floating = {};
  let floatIndex = 0;
  for (const p of panels) {
    if (p.defaultDock === FLOAT) floating[p.id] = defaultFloatRect(p.id, floatIndex++);
    else tabs[p.defaultDock].push(p.id);
  }
  return {
    version: LAYOUT_VERSION,
    sizes: { ...DEFAULT_SIZES },
    collapsed: { left: false, right: false, bottom: false },
    tabs,
    active: { left: tabs.left[0] || null, right: tabs.right[0] || null, bottom: tabs.bottom[0] || null },
    floating,
  };
}

/** Cheap structural check, also used by the localStorage migration. */
export function isLayoutShapeValid(raw) {
  return isObj(raw) && raw.version === LAYOUT_VERSION && isObj(raw.sizes) && isObj(raw.collapsed)
    && isObj(raw.tabs) && DOCKS.every((d) => Array.isArray(raw.tabs[d])) && isObj(raw.floating);
}

/**
 * Turn whatever was stored into a valid layout. A wrong overall shape resets to the
 * default; individual bad fields are repaired; unknown panel ids are dropped; panels
 * added to the registry since the layout was saved go to their default dock.
 */
export function normalizeLayout(raw, panels = PANELS) {
  const base = createDefaultLayout(panels);
  if (!isLayoutShapeValid(raw)) return base;
  const known = new Set(panels.map((p) => p.id));
  const seen = new Set();
  const out = { version: LAYOUT_VERSION, sizes: {}, collapsed: {}, tabs: { left: [], right: [], bottom: [] }, active: {}, floating: {} };

  for (const d of DOCKS) {
    out.sizes[d] = num(raw.sizes[d]) ? clampSize(d, raw.sizes[d]) : base.sizes[d];
    out.collapsed[d] = raw.collapsed[d] === true;
    for (const id of raw.tabs[d]) {
      if (typeof id === 'string' && known.has(id) && !seen.has(id)) { seen.add(id); out.tabs[d].push(id); }
    }
  }
  for (const [id, rect] of Object.entries(raw.floating)) {
    const p = panels.find((x) => x.id === id);
    const r = cleanRect(rect);
    if (p && p.floatable && r && !seen.has(id)) { seen.add(id); out.floating[id] = r; }
  }
  for (const p of panels) {
    if (seen.has(p.id)) continue;
    if (p.defaultDock === FLOAT) out.floating[p.id] = base.floating[p.id];
    else out.tabs[p.defaultDock].push(p.id);
  }
  for (const d of DOCKS) {
    const a = isObj(raw.active) ? raw.active[d] : null;
    out.active[d] = out.tabs[d].includes(a) ? a : (out.tabs[d][0] || null);
  }
  return out;
}

function without(list, id) { return list.filter((x) => x !== id); }

/** Remove a panel from every dock and from the floating set. */
function detach(layout, id) {
  const tabs = {};
  const active = { ...layout.active };
  for (const d of DOCKS) {
    tabs[d] = without(layout.tabs[d], id);
    if (active[d] === id) active[d] = tabs[d][0] || null;
  }
  const floating = { ...layout.floating };
  delete floating[id];
  return { ...layout, tabs, active, floating };
}

/** Where a panel currently lives: 'left' | 'right' | 'bottom' | 'float' | null. */
export function dockOf(layout, id) {
  if (layout.floating[id]) return FLOAT;
  return DOCKS.find((d) => layout.tabs[d].includes(id)) || null;
}

export function moveToDock(layout, id, dock) {
  if (!getPanel(id) || !DOCKS.includes(dock)) return layout;
  const next = detach(layout, id);
  return {
    ...next,
    tabs: { ...next.tabs, [dock]: [...next.tabs[dock], id] },
    active: { ...next.active, [dock]: id },
    collapsed: { ...next.collapsed, [dock]: false },
  };
}

export function floatPanel(layout, id, rect) {
  const p = getPanel(id);
  if (!p || !p.floatable) return layout;
  const r = cleanRect(rect) || cleanRect(layout.floating[id]) || defaultFloatRect(id, Object.keys(layout.floating).length);
  const next = detach(layout, id);
  return { ...next, floating: { ...next.floating, [id]: r } };
}

export function reorderTab(layout, id, toIndex) {
  const dock = DOCKS.find((d) => layout.tabs[d].includes(id));
  if (!dock) return layout;
  const list = without(layout.tabs[dock], id);
  const i = Math.min(list.length, Math.max(0, toIndex));
  list.splice(i, 0, id);
  return { ...layout, tabs: { ...layout.tabs, [dock]: list } };
}

/**
 * The tab a dock shows: `active` when it is open, else the most recently used open tab
 * (`recent`, newest first), else the first one. Closing Grid math with Analysis still open
 * falls back to Analysis, not to whatever tab comes first in the dock (Field Agents).
 */
export function pickActiveTab(ids, active, recent = []) {
  if (ids.includes(active)) return active;
  return recent.find((id) => ids.includes(id)) || ids[0];
}

/** `recent` with `id` moved to the front (most recently used first, capped). */
export function touchRecent(recent, id, max = 32) {
  if (!id) return recent;
  return [id, ...recent.filter((r) => r !== id)].slice(0, max);
}

export function layoutReducer(state, action) {
  switch (action.type) {
    case 'moveTo': return moveToDock(state, action.id, action.dock);
    case 'float': return floatPanel(state, action.id, action.rect);
    case 'reorder': return reorderTab(state, action.id, action.index);
    case 'setSize':
      if (!DOCKS.includes(action.dock) || !num(action.size)) return state;
      return { ...state, sizes: { ...state.sizes, [action.dock]: clampSize(action.dock, action.size) } };
    case 'setCollapsed':
      if (!DOCKS.includes(action.dock)) return state;
      return { ...state, collapsed: { ...state.collapsed, [action.dock]: !!action.value } };
    case 'toggleCollapsed':
      if (!DOCKS.includes(action.dock)) return state;
      return { ...state, collapsed: { ...state.collapsed, [action.dock]: !state.collapsed[action.dock] } };
    case 'setActive':
      if (!DOCKS.includes(action.dock) || !state.tabs[action.dock].includes(action.id)) return state;
      return { ...state, active: { ...state.active, [action.dock]: action.id } };
    case 'setFloatRect': {
      const r = cleanRect(action.rect);
      if (!r || !state.floating[action.id]) return state;
      return { ...state, floating: { ...state.floating, [action.id]: r } };
    }
    case 'reveal': { // a panel was just opened: make it the active tab of its dock and expand the dock
      const dock = DOCKS.find((d) => state.tabs[d].includes(action.id));
      if (!dock) return state;
      if (state.active[dock] === action.id && !state.collapsed[dock]) return state;
      return { ...state, active: { ...state.active, [dock]: action.id }, collapsed: { ...state.collapsed, [dock]: false } };
    }
    case 'reset': return createDefaultLayout();
    default: return state;
  }
}

/** localStorage helpers. Both swallow storage errors (private mode, quota). */
export function loadLayout(storage) {
  try {
    const s = storage || window.localStorage;
    const raw = s.getItem(LAYOUT_KEY);
    if (!raw) return createDefaultLayout();
    return normalizeLayout(JSON.parse(raw));
  } catch {
    return createDefaultLayout();
  }
}

export function saveLayout(layout, storage) {
  try {
    (storage || window.localStorage).setItem(LAYOUT_KEY, JSON.stringify(layout));
    return true;
  } catch {
    return false;
  }
}
